// GENERATED — DO NOT HAND-EDIT.
//
// Source of truth: specs/seeds/dashboard-v2/auth-flows/sso-callback.yaml
// Generator: tools/v2-gen/v2genauth.py
// Regenerate via: make v2-generate-auth-pages
//
// Hand-edits to non-override-block content WILL be lost on next
// regeneration. See specs/_product/dashboard-v2-auth-flow-spec.md §5
// for override blocks (imports / helpers / validation / success-action /
// state-render).

import { useState, useEffect } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { apiClient } from "../../api/client";
import { AuthLayout } from "./AuthLayout";
import { errorStyle, ctaLinkStyle } from "./styles";
// override-block:imports BEGIN
// override-block:imports END

function extractProvider(searchProvider: string | null, ssoState: string): string | null {
  // v1 OAuth-state-prefix convention (web/dashboard/src/pages/auth/
  // SsoCallbackPage.tsx extractProviderFromState): state encodes provider
  // as `<provider>:<random>` when no explicit ?provider= is present.
  if (searchProvider) return searchProvider;
  if (ssoState && ssoState.includes(":")) {
    const head = ssoState.split(":", 1)[0];
    if (head) return head;
  }
  return null;
}

// override-block:helpers BEGIN
// override-block:helpers END

type State = "exchanging" | "success" | "error";

export default function SsoCallbackPage() {
  const [params] = useSearchParams();
  const code = params.get("code") ?? "";
  const ssoState = params.get("state") ?? "";
  const provider = extractProvider(params.get("provider"), ssoState) ?? "";
  const [state, setState] = useState<State>("exchanging");
  const [error, setError] = useState<string>("");
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;
    async function run() {
      if (!code) {
        if (!cancelled) {
          setError("SSO callback is missing the authorization code. Restart sign-in.");
          setState("error");
        }
        return;
      }
      if (!provider) {
        if (!cancelled) {
          setError("SSO callback is missing the provider parameter. Restart sign-in from the provider link.");
          setState("error");
        }
        return;
      }
      try {
        const res = await apiClient().post(`/auth/sso/${provider}/callback`, { code: code, state: ssoState });
        if (!cancelled) {
          // override-block:success-action BEGIN
          // override-block:success-action END
          const _v_auth_token_dev: string | undefined = res.data?.access_token;
          if (_v_auth_token_dev) {
            window.localStorage.setItem("auth_token_dev", _v_auth_token_dev);
          }
          setState("success");
          window.setTimeout(() => navigate("/", { replace: true }), 600);
        }
      } catch (e: any) {
        if (!cancelled) {
          setError(e?.response?.data?.detail ?? "Sign-in failed. The provider may have rejected the request — please try again.");
          setState("error");
        }
      }
    }
    void run();
    return () => {
      cancelled = true;
    };
  }, [code, ssoState, provider, navigate]);

  // override-block:state-render BEGIN
  // override-block:state-render END

  if (state === "exchanging") {
    return (
      <AuthLayout pageId="sso-callback" title={"Completing sign-in…"}>
        <p data-role="spinner" style={{ fontSize: 14, color: "var(--color-text)", textAlign: "center" }}>
          Finishing your single sign-on…
        </p>
      </AuthLayout>
    );
  }

  if (state === "success") {
    return (
      <AuthLayout pageId="sso-callback" title={"Signed in"}>
        <p data-role="success" style={{ fontSize: 14, color: "var(--color-text)", textAlign: "center" }}>
          Redirecting you to your dashboard…
        </p>
      </AuthLayout>
    );
  }

  if (state === "error") {
    return (
      <AuthLayout pageId="sso-callback" title={"Sign-in failed"}>
        <div data-role="error" style={errorStyle}>{error}</div>
        <Link to="/login" data-action="goto-login" style={ctaLinkStyle()}>
          Back to sign-in
        </Link>
      </AuthLayout>
    );
  }

  return null;
}
